import { Box, Text, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import clsx from 'clsx';
import { FC } from 'react';
import s from './ShowMoreText.module.scss';
import { useShowMoreText } from './useShowMoreText';

type Props = {
  originalText: string | undefined;
  dataHookPrefix?: string;
  textSize?: 'small' | 'medium';
  handleShowMoreClick?: () => void;
  handleShowLessClick?: () => void;
  showMoreInitialState?: boolean;
  isMobileView?: boolean;
  maxChars?: number;
  maxLines?: number;
};

const ShowMoreText: FC<Props> = ({
  originalText,
  dataHookPrefix = '',
  textSize = 'small',
  isMobileView = false,
  maxChars,
  maxLines,
  showMoreInitialState,
  handleShowMoreClick,
  handleShowLessClick,
}) => {
  const { t } = useTranslation();
  const {
    showingMore,
    needsTruncation,
    truncatedText,
    remainingText,
    handleToggle,
  } = useShowMoreText({
    originalText,
    maxChars,
    maxLines,
    showMoreInitialState,
    handleShowMoreClick,
    handleShowLessClick,
  });

  const commonTextProps = {
    size: textSize,
    weight: 'thin' as const,
    style: { wordBreak: 'break-word' as const },
    dataHook: `${dataHookPrefix}show-more-text`,
  };

  const buttonProps = {
    dataHook: `${dataHookPrefix}show-more-text-button`,
    skin: 'dark' as const,
    underline: 'always' as const,
    size: textSize,
    weight: 'normal' as const,
    onClick: handleToggle,
    children: showingMore
      ? t('show-less-text-button')
      : t('show-more-text-button'),
  };

  if (!originalText) return null;

  const textContentClassName = clsx(
    s.textContent,
    maxLines && !showingMore && !isMobileView
      ? s.lineClamped
      : showingMore
      ? s.expanded
      : '',
  );

  return (
    <Box
      className={clsx(s.showMoreTextContainer, isMobileView && s.mobile)}
      dataHook="show-more-text-container"
      direction={isMobileView ? undefined : 'vertical'}
    >
      <Box
        className={textContentClassName}
        style={{ '--max-lines': maxLines } as React.CSSProperties}
      >
        <Text {...commonTextProps}>
          {truncatedText}
          <span className={remainingText && !showingMore ? '' : s.hide}>
            ...
          </span>
          <span
            className={remainingText && showingMore ? '' : s.hide}
            data-hook={`${dataHookPrefix}show-more-text-extra`}
          >
            {remainingText.trimEnd()}
          </span>
          {isMobileView && needsTruncation && (
            <TextButton
              {...buttonProps}
              className={clsx(s.moreLessButton, s.mobile)}
            />
          )}
        </Text>
      </Box>
      {!isMobileView && needsTruncation && (
        <TextButton {...buttonProps} className={s.moreLessButton} />
      )}
    </Box>
  );
};

export default ShowMoreText;
